import { addEventModifier } from './dynatraceReport';

const IS_DT_EVENT_MODIFIER_REGISTERED_KEY = 'IS_DT_EVENT_MODIFIER_REGISTERED';

declare global {
  interface Window {
    [IS_DT_EVENT_MODIFIER_REGISTERED_KEY]?: boolean;
  }
}

// The Dynatrace bootstrap loader fetches the real agent (window.dynatrace)
// asynchronously, so script.onload can fire before the agent API exists.
// We poll briefly to bridge that gap so very-early errors are still filtered
// and enriched.
export const DYNATRACE_READY_POLL_INTERVAL_MS = 500;
export const DYNATRACE_READY_TIMEOUT_MS = 30000;

let registrationIntervalId: ReturnType<typeof setInterval> | null = null;

function stopPolling() {
  if (registrationIntervalId !== null) {
    clearInterval(registrationIntervalId);
    registrationIntervalId = null;
  }
}

function tryRegister(): boolean {
  if (!window.dynatrace?.addEventModifier) {
    return false;
  }
  try {
    // event modifier adds contextual attributes to OOTB and custom events that
    // Dynatrace detects. event = the Dynatrace Event created;
    // context = the JS Event that triggered it.
    window.dynatrace.addEventModifier(addEventModifier);
    window[IS_DT_EVENT_MODIFIER_REGISTERED_KEY] = true;
    return true;
  } catch (e) {
    console.error(`Failed to register Dynatrace addEventModifier: ${e}`);
    stopPolling();
    return false;
  }
}

export function registerAddEventModifier() {
  if (
    window[IS_DT_EVENT_MODIFIER_REGISTERED_KEY] ||
    registrationIntervalId !== null
  ) {
    return;
  }

  if (tryRegister()) {
    return;
  }

  const startedAt = Date.now();
  registrationIntervalId = setInterval(() => {
    if (tryRegister()) {
      stopPolling();
      return;
    }
    if (Date.now() - startedAt >= DYNATRACE_READY_TIMEOUT_MS) {
      stopPolling();
      console.error(
        'Dynatrace agent did not become ready within timeout; addEventModifier was not registered.',
      );
    }
  }, DYNATRACE_READY_POLL_INTERVAL_MS);
}
